// SPDX-License-Identifier: AGPL-3.0-or-later

import {COSTUME_HEADROOM_LAYOUT_PX} from '@app/features/channel/components/ChannelMessageStreamUtils';
import {CrosspostCommunityPopout} from '@app/features/channel/components/CrosspostCommunityPopout';
import {useMaybeMessageViewContext} from '@app/features/channel/components/MessageViewContext';
import {PreloadableUserPopout} from '@app/features/channel/components/PreloadableUserPopout';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import MessageReactions from '@app/features/messaging/state/MessageReactions';
import {Avatar} from '@app/features/ui/components/Avatar';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import type {User} from '@app/features/user/models/User';
import UserSettings from '@app/features/user/state/UserSettings';
import {observer} from 'mobx-react-lite';
import type {CSSProperties} from 'react';
import {useCallback, useMemo, useSyncExternalStore} from 'react';

function countMessagesWithReactions(messageIds: ReadonlyArray<string>): number {
	let rows = 0;
	for (const messageId of messageIds) {
		if (MessageReactions.getMessageReactions(messageId).length > 0) rows += 1;
	}
	return rows;
}

function useCostumeHeadroomReactionRows(messageIds: ReadonlyArray<string> | null | undefined): number {
	const idsKey = messageIds && messageIds.length > 0 ? messageIds.join('\0') : '';
	const subscribe = useCallback(
		(onStoreChange: () => void) => {
			if (!idsKey) return () => {};
			const ids = idsKey.split('\0');
			const unsubs = ids.map((messageId) => MessageReactions.subscribeMessage(messageId, onStoreChange));
			return () => {
				for (const unsub of unsubs) unsub();
			};
		},
		[idsKey],
	);
	const getSnapshot = useCallback(() => {
		if (!idsKey) return 0;
		return countMessagesWithReactions(idsKey.split('\0'));
	}, [idsKey]);
	return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export const MessageAvatar = observer(
	({
		user,
		message,
		guildId,
		size,
		className,
		isHovering,
	}: {
		user: User;
		message: Message;
		guildId?: string;
		size: 16 | 24 | 32 | 40 | 48 | 80 | 120;
		className: string;
		isHovering: boolean;
		isPreview: boolean;
	}) => {
		const messageView = useMaybeMessageViewContext();
		const onPopoutToggle = messageView?.onPopoutToggle;
		const handlePopoutOpen = useCallback(() => onPopoutToggle?.(true), [onPopoutToggle]);
		const handlePopoutClose = useCallback(() => onPopoutToggle?.(false), [onPopoutToggle]);
		const renderReactions = UserSettings.renderReactions;
		const reactionRows = useCostumeHeadroomReactionRows(
			renderReactions ? messageView?.costumeHeadroomReactionMessageIds : null,
		);
		const costumeHeadroomPx =
			messageView?.costumeHeadroomPx == null
				? null
				: messageView.costumeHeadroomPx + reactionRows * COSTUME_HEADROOM_LAYOUT_PX.reactionRow;
		const avatarStyle = useMemo((): CSSProperties | undefined => {
			if (costumeHeadroomPx == null) return undefined;
			return {'--avatar-costume-headroom': String(costumeHeadroomPx)} as CSSProperties;
		}, [costumeHeadroomPx]);
		if (message.isCrosspostCopy) {
			return (
				<CrosspostCommunityPopout
					message={message}
					onPopoutOpen={handlePopoutOpen}
					onPopoutClose={handlePopoutClose}
					data-flx="channel.message-avatar.crosspost-community-popout"
				>
					<FocusRing data-flx="channel.message-avatar.focus-ring--2">
						<Avatar
							user={user}
							size={size}
							className={className}
							forceAnimate={isHovering}
							guildId={guildId}
							data-user-id={user.id}
							data-guild-id={guildId}
							data-flx="channel.message-avatar.crosspost-author-avatar"
						/>
					</FocusRing>
				</CrosspostCommunityPopout>
			);
		}
		return (
			<PreloadableUserPopout
				user={user}
				isWebhook={message.webhookId != null}
				webhookId={message.webhookId ?? undefined}
				guildId={guildId}
				channelId={message.channelId}
				message={message}
				enableLongPressActions={false}
				onPopoutOpen={handlePopoutOpen}
				onPopoutClose={handlePopoutClose}
				data-flx="channel.message-avatar.preloadable-user-popout"
			>
				<FocusRing data-flx="channel.message-avatar.focus-ring">
					<Avatar
						user={user}
						size={size}
						className={className}
						style={avatarStyle}
						forceAnimate={isHovering}
						guildId={guildId}
						data-user-id={user.id}
						data-guild-id={guildId}
						data-flx="channel.message-avatar.avatar"
					/>
				</FocusRing>
			</PreloadableUserPopout>
		);
	},
);
